/**
 * Owns the notification stream for the whole tab.
 *
 * `useNotificationStream` used to be called from `NotificationSidebarItem`, which lives inside
 * the sidebar, which `Layout` mounts conditionally on the route (see IGNORE_SIDEBAR). Clicking
 * the logo routes to '/', a path on that list, so the sidebar unmounted, the EventSource died
 * with it, and the redirect onward rebuilt the lot: a new stream, a fresh tray hydrate and a
 * fresh unread count. Captured live on 2026-09-19, one click, three requests, and 852 stream
 * opens an hour across the org at peak.
 *
 * Mounted once above the router, the connection's lifetime is the tab's, whatever the sidebar
 * does on the way.
 *
 * Must sit inside `QueryProvider`: the hook reads and invalidates TanStack queries.
 */
import { createContext, useContext, type ReactNode } from 'react';

import { useNotificationStream, type UseNotificationStreamResult } from './useNotificationStream';

const NotificationStreamContext = createContext<UseNotificationStreamResult | null>(null);

interface Props {
  /** Open a connection at all. False wherever there is no session — see the hook's option. */
  enabled?: boolean;
  children: ReactNode;
}

export function NotificationStreamProvider({ enabled = true, children }: Props) {
  const stream = useNotificationStream({ enabled });
  return <NotificationStreamContext.Provider value={stream}>{children}</NotificationStreamContext.Provider>;
}

/**
 * The tab's stream. Throws outside the provider rather than silently opening a second
 * connection, which is the failure this whole file exists to prevent.
 */
export function useNotificationStreamContext(): UseNotificationStreamResult {
  const stream = useContext(NotificationStreamContext);
  if (!stream) {
    throw new Error('useNotificationStreamContext must be used inside <NotificationStreamProvider>');
  }
  return stream;
}
